import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { GuestInfo } from '@/shared/types';

interface UpdateMyGuestDetailsResponse {
  complete: boolean;
  missing: string[];
}

/**
 * Lets the signed-in student fill in the personal details missing from their
 * booking, via the updateMyGuestDetails Cloud Function.
 *
 * Goes through a callable because Firestore rules restrict booking updates to
 * admins, and because a single entry of the `guests` array cannot be patched in
 * place. The function resolves the booking from the caller's own student record,
 * so no booking id is sent from here.
 */
export const useUpdateMyGuestDetails = () => {
  const queryClient = useQueryClient();
  const updateFn = httpsCallable<{ guest: GuestInfo }, UpdateMyGuestDetailsResponse>(
    getFunctions(undefined, 'europe-west1'),
    'updateMyGuestDetails'
  );

  return useMutation({
    mutationFn: async (guest: GuestInfo): Promise<UpdateMyGuestDetailsResponse> => {
      const result = await updateFn({ guest });
      return result.data;
    },
    onSuccess: () => {
      // The guard reads the booking to decide whether to keep prompting, and
      // the admin views read the same doc.
      queryClient.invalidateQueries({ queryKey: ['my-guest-details'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
};
